import React, { Component } from 'react';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import FormikTextField from 'components/theme/formik/text-field';
import Typography from '@material-ui/core/Typography';
import Switch from 'components/theme/switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Divider from '@material-ui/core/Divider';
import { Formik, Field, FieldArray } from 'formik';

import { withStyles } from '@material-ui/styles';
import DialogConfirmation from 'components/theme/dialog-confirmation';
import { flags, actions, messages } from 'app-constants';
import ConditionForm from 'components/features/condition/condition-form';
import FeatureFormToggle from 'components/features/feature-form/feature-form-toggle';
import FeatureFormDelete from 'components/features/feature-form/feature-form-delete';
import DisplayEnv from 'components/theme/display-env';
import validationSchema from './validation-schema';

const styles = (theme) => ({
  chip: {
    marginLeft: theme.spacing.unit * 2
  },
  conditionDivider: {
    margin: [[theme.spacing.unit * 2, 0]]
  },
  controlLabel: {
    margin: [[0]]
  },
  noCursor: {
    cursor: 'default'
  }
});

function FormConrolFeatureKey(props) {
  return (
    <Field
      name="key"
      component={FormikTextField}
      placeholder="Enter feature key"
      required
      label="Key"
      {...props}
    />
  );
}

function FormControlDescription() {
  return (
    <Field
      name="description"
      component={FormikTextField}
      placeholder="Enter feature description"
      label="Description"
      multiline
    />
  );
}

function FormControlToggle({ enable, classes, onChange }) {
  return (
    <FormControlLabel
      control={
        <Switch
          checked={enable}
          name="enable"
          onChange={onChange('enable')}
          color="primary"
        />
      }
      label="Enable"
      labelPlacement="start"
      className={classes.controlLabel}
    />
  );
}

function DisplayNote({ children }) {
  return (
    <Typography variant="subtitle2" gutterBottom>
      {children}
    </Typography>
  );
}

function FormControlCondition({
  classes,
  conditions,
  userSegments,
  refetchUserSegments,
  environment
}) {
  return (
    <>
      <Divider className={classes.conditionDivider} />

      <DisplayNote>
        Changes to this will only impact this flag in{' '}
        <DisplayEnv environment={environment} />
        environment
      </DisplayNote>

      <FieldArray
        name="conditions"
        render={() => {
          return (
            <>
              {conditions.map((condition, index) => (
                <div key={condition.uId}>
                  <ConditionForm
                    condition={condition}
                    index={index}
                    userSegments={userSegments}
                    refetchUserSegments={refetchUserSegments}
                  />
                </div>
              ))}
            </>
          );
        }}
      />
    </>
  );
}

class FormContent extends Component {
  state = { openConfirmation: false };

  handleConfirmationClose = (confirmation) => {
    if (confirmation === 'ok') {
      this.props.onFormClose();
    }
    this.setState({ openConfirmation: false });
  };

  onCancel = () => {
    if (this.props.dirty) {
      this.setState({ openConfirmation: true });
    } else {
      this.props.onFormClose();
    }
  };

  onChange = (tagetName) => (e) => {
    this.props.handleChange(e);
    this.props.setFieldTouched(tagetName, true, false);
  };

  render() {
    const {
      values: { enable, environment, conditions },
      isSubmitting,
      isValid,
      submitForm
    } = this.props;

    const { userSegments, classes, action, refetchUserSegments } = this.props;

    const { openConfirmation } = this.state;

    return (
      <>
        {openConfirmation ? (
          <DialogConfirmation
            title={`${
              action === actions.FEATURE_ADD ? 'Add' : 'Update'
            } Feature`}
            cancelLabel="Continue"
            okLabel="Discard"
            open={openConfirmation}
            onClose={this.handleConfirmationClose}
          >
            Your changes are not saved. Closing this window will discard
            changes.
          </DialogConfirmation>
        ) : (
          ''
        )}

        <DialogContent>
          {action === actions.FEATURE_EDIT && (
            <>
              <DisplayNote>{messages.SETTINGS_ALL_ENV_NOTE}</DisplayNote>
              <FormConrolFeatureKey disabled />
              <FormControlDescription />
              <FormControlToggle
                enable={enable}
                classes={classes}
                onChange={this.onChange}
              />

              {flags.IsConditionShow() &&
                conditions &&
                conditions.length > 0 && (
                  <FormControlCondition
                    classes={classes}
                    conditions={conditions}
                    userSegments={userSegments}
                    refetchUserSegments={refetchUserSegments}
                    environment={environment}
                  />
                )}
            </>
          )}

          {action === actions.FEATURE_ADD && (
            <>
              <DisplayNote>{messages.DEFAULT_ADD_FEATURE_NOTE}</DisplayNote>
              <FormConrolFeatureKey />
              <FormControlDescription />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={this.onCancel}
            color="secondary"
            name="cancel"
            data-testid="cancel-form"
          >
            Cancel
          </Button>
          <Button
            onClick={submitForm}
            color="primary"
            name="submit-feature"
            disabled={!isValid || isSubmitting}
            variant="contained"
            data-testid="submit-form"
          >
            {action === actions.FEATURE_ADD ? 'Add' : 'Update'}
          </Button>
        </DialogActions>
      </>
    );
  }
}

const FormContentWithStyles = withStyles(styles, { withTheam: true })(
  FormContent
);

export default function FeatureFormWithFormik({
  feature,
  flag,
  onSubmit,
  onClose,
  action,
  userSegments = [],
  conditions,
  refetchUserSegments
}) {
  const IsTargetAppVersionShow = flags.IsTargetAppVerisonShow();

  if (action === actions.FEATURE_TOGGLE) {
    return (
      <FeatureFormToggle
        onSubmit={onSubmit}
        onFormClose={onClose}
        feature={feature}
        flag={flag}
      />
    );
  }

  if (action === actions.FEATURE_DELETE) {
    return (
      <FeatureFormDelete
        onSubmit={onSubmit}
        onFormClose={onClose}
        feature={feature}
      />
    );
  }

  let conditionList = conditions;
  if (!conditionList) {
    conditionList = [];
  }

  if (action === actions.FEATURE_EDIT) {
    const hasTargetUserCondition = conditionList.find(
      (c) => c.conditionType === 'TARGET_USER'
    );
    const hasVersionTargetCondition = conditionList.find(
      (c) => c.conditionType === 'TARGET_APP_VERSION'
    );

    if (!hasTargetUserCondition) {
      conditionList.push({
        appId: feature.appId,
        conditionRule: 'IN',
        conditionTarget: 'None',
        conditionType: 'TARGET_USER',
        enable: false,
        environment: flag.environment,
        featureKey: feature.key,
        uId: 'default-condition-target-user'
      });
    }

    if (!hasVersionTargetCondition && IsTargetAppVersionShow) {
      conditionList.push({
        appId: feature.appId,
        conditionRule: 'EQ',
        conditionTarget: '',
        conditionType: 'TARGET_APP_VERSION',
        enable: false,
        environment: flag.environment,
        featureKey: feature.key,
        uId: 'default-condition-target-version'
      });
    }

    conditionList.forEach((c) => {
      if (!c.conditionTarget) {
        c.conditionTarget = '';
      }
    });
  }

  if (!IsTargetAppVersionShow) {
    conditionList = conditionList.filter(
      (c) => c.conditionType !== 'TARGET_APP_VERSION'
    );
  }

  const initialValues = {
    description: (feature && feature.desc) || '',
    key: (feature && feature.key) || '',
    enable: Boolean(flag && flag.enable),
    environment: flag && flag.environment,
    conditions: conditionList
  };

  const validationSchemaObj = validationSchema(action);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchemaObj}
      onSubmit={onSubmit}
      render={(formikProps) => (
        <FormContentWithStyles
          {...formikProps}
          onFormClose={onClose}
          action={action}
          userSegments={userSegments}
          conditions={conditionList}
          refetchUserSegments={refetchUserSegments}
        />
      )}
    />
  );
}
